import React from 'react';
import { X_PATH } from 'constants/xPath';
import { useTranslation } from 'react-i18next';
import isNumber from 'lodash/isNumber';
import MdtuTable from '#web-components/components/Table';
import { Order, ColumnDefinition, FilterParams } from '#web-components/types/table';

interface Props<T> {
  columnDefinitions: ColumnDefinition[];
  list: T[];
  order: Order;
  orderField: string;
  onOrderChange?: (orderField: string, order: Order) => void;
  onFilterChange?: (filterParams: FilterParams) => void;
  emptyPlaceholder?: string;
  emptyPlaceholderIcon?: string | JSX.Element;
  hideEmptyPlaceholder?: boolean;
  pagination?: {
    page: number;
    rowsPerPage: number;
    totalItems?: number;
  };
  dataXpathTableRowPropName?: string;
}

export default function Table<T>(props: Props<T>) {
  const { t, i18n } = useTranslation('components', { keyPrefix: 'table' });
  const {
    columnDefinitions,
    list,
    order,
    orderField,
    onOrderChange,
    emptyPlaceholder,
    emptyPlaceholderIcon,
    hideEmptyPlaceholder,
    pagination,
    onFilterChange,
    dataXpathTableRowPropName,
  } = props;
  return (
    <MdtuTable
      columnDefinitions={columnDefinitions}
      list={list}
      order={order}
      orderField={orderField}
      onOrderChange={onOrderChange}
      emptyPlaceholder={emptyPlaceholder || t('emptyPlaceholder')}
      emptyPlaceholderIcon={emptyPlaceholderIcon}
      hideEmptyPlaceholder={hideEmptyPlaceholder}
      header={{
        'data-xpath': X_PATH.tableHeadCell,
      }}
      onFilterChange={onFilterChange}
      pagination={{
        ...pagination,
        labelRowsPerPage: t('perPage'),
        getDisplayedRowsLabel: (params) => (isNumber(params.count)
          ? t('pagination', params)
          : t('paginationNoCount', params)),
        'data-xpath': {
          tablePageCountSelect: X_PATH.tablePageCountSelect,
          tableFirstPage: X_PATH.tableFirstPage,
          tablePrevPage: X_PATH.tablePrevPage,
          tableNextPage: X_PATH.tableNextPage,
          tableLastPage: X_PATH.tableLastPage,
        },
        ariaLabel: {
          firstPage: t('aria.firstPage'),
          prevPage: t('aria.prevPage'),
          nextPage: t('aria.nextPage'),
          lastPage: t('aria.lastPage'),
        },
      }}
      data-xpath={{
        tableContainer: X_PATH.tableContainer,
        tableRow: X_PATH.tableRow,
      }}
      dataXpathTableRowPropName={dataXpathTableRowPropName}
      locale={i18n.language}
    />
  );
}
